'use client';

import { OTPInput, OTPInputContext, REGEXP_ONLY_DIGITS } from 'input-otp';
import { ChangeEvent, ComponentProps, FC, InputHTMLAttributes, useContext } from 'react';
import { cn } from 'utils/cn';

import './styles.scss';

function InputOTP({ className, ...props }: ComponentProps<typeof OTPInput>) {
  return (
    <OTPInput
      data-slot="input-otp"
      containerClassName={cn('flex items-center gap-2 has-disabled:opacity-50')}
      className={cn('disabled:cursor-not-allowed', className)}
      {...props}
    />
  );
}

function InputOTPGroup({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div data-slot="input-otp-group" className={cn('flex items-center', className)} {...props} />
  );
}

function InputOTPSlot({
  index,
  className,
  ...props
}: ComponentProps<'div'> & {
  index: number;
}) {
  const inputOTPContext = useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots?.[index] ?? {};

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      className={cn(
        'data-[active=true]:border-ring',
        'data-[active=true]:border-primary-blue-200',
        'data-[active=true]:ring-4 data-[active=true]:ring-primary-blue-900',
        'data-[active=true]:aria-invalid:ring-destructive/20',
        'dark:data-[active=true]:aria-invalid:ring-destructive/40',
        'aria-invalid:border-destructive',
        'data-[active=true]:aria-invalid:border-destructive',
        'dark:bg-input/30 border-input shadow-xs relative flex items-center',
        'justify-center outline-none transition-all',
        'data-[active=true]:z-10',
        'input-code',
        className
      )}
      {...props}
    >
      {!!char && char}
      {!char && (
        <span className={isActive ? 'text-primary-blue-900' : 'text-tertiary-800'}>{0}</span>
      )}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="animate-caret-blink bg-foreground h-4 w-px duration-1000" />
        </div>
      )}
    </div>
  );
}

interface IInputCode extends InputHTMLAttributes<HTMLInputElement> {
  value?: string;
  name?: string;
}

const InputCode: FC<IInputCode> = ({ value, name, onChange, onBlur }) => {
  const handleChange = (newValue: string) => {
    onChange?.({ target: { value: newValue, name } } as ChangeEvent<HTMLInputElement>);
  };

  return (
    <InputOTP
      maxLength={6}
      pattern={REGEXP_ONLY_DIGITS}
      value={value}
      onChange={handleChange}
      autoFocus
      onBlur={onBlur}
      name={name}
    >
      <InputOTPGroup>
        <InputOTPSlot index={0} />
        <InputOTPSlot index={1} />
        <InputOTPSlot index={2} />
        <InputOTPSlot index={3} />
        <InputOTPSlot index={4} />
        <InputOTPSlot index={5} />
      </InputOTPGroup>
    </InputOTP>
  );
};

export default InputCode;
